"use client";

import {
  type ColumnDef,
  createSortedRowModel,
  type RowData,
  rowSortingFeature,
  type SortingState,
  sortFn_alphanumeric,
  tableFeatures,
  useTable,
} from "@tanstack/react-table";
import * as React from "react";

import { cn } from "../../cn";
import { FOCUS_RING_INSET } from "../focus-ring";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "../table";
import { useRowWindow, WINDOW_AFTER } from "./row-window";

// The table view (spec M2 "views table"; design spec §6.4 table recipe): TanStack Table v9 for
// the row model and sorting, this package's `Table` parts for every pixel. Columns are plain
// descriptors (accessor + optional cell renderer); the first column is the row header and, with
// `onRowOpen`, the row's one button. Sorting is client-side, controlled or not; nothing fetches.

export type TableViewColumn<T> = {
  id: string;
  header: string;
  /** The sortable value. */
  accessor: (row: T) => string | number | null | undefined;
  /** Display; defaults to the accessor's value. */
  cell?: (row: T) => React.ReactNode;
  numeric?: boolean;
  sortable?: boolean;
  className?: string;
  /**
   * BOARD-ORDER: controls before the first column's open button (a drag grip, a colour
   * picker): outside the button, so they are never a control nested in a control.
   */
  prefix?: (row: T) => React.ReactNode;
};

export type TableViewSort = { id: string; desc: boolean } | null;

export type TableViewProps<T> = Omit<React.ComponentProps<"div">, "children"> & {
  rows: T[];
  columns: TableViewColumn<T>[];
  getRowId: (row: T) => string;
  onRowOpen?: (row: T) => void;
  selectedRowId?: string;
  /** Controlled sort; pair with `onSortChange`. */
  sort?: TableViewSort;
  defaultSort?: TableViewSort;
  onSortChange?: (sort: TableViewSort) => void;
  caption?: React.ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
  /** Accessible name of the table. */
  label?: string;
  /**
   * BOARD-ORDER: extra attributes and handlers per body row (drag-and-drop reorder: `draggable`,
   * the drag events, a drop-indicator class). The row keeps its own key, id and selection.
   */
  rowProps?: (row: T) => Omit<React.ComponentProps<"tr">, "key" | "children">;
};

const features = tableFeatures({
  rowSortingFeature,
  sortedRowModel: createSortedRowModel(),
  sortFns: { alphanumeric: sortFn_alphanumeric },
});

const toState = (s: TableViewSort | undefined): SortingState => (s ? [s] : []);

export function TableView<T extends RowData>({
  rows,
  columns,
  getRowId,
  onRowOpen,
  selectedRowId,
  sort,
  defaultSort = null,
  onSortChange,
  caption,
  emptyTitle = "Nothing to show",
  emptyDescription,
  label,
  rowProps,
  className,
  ...props
}: TableViewProps<T>) {
  const [ownSort, setOwnSort] = React.useState<TableViewSort>(defaultSort);
  const current = sort !== undefined ? sort : ownSort;

  const columnDefs = React.useMemo(
    () =>
      columns.map(
        (c) =>
          ({
            id: c.id,
            header: c.header,
            accessorFn: (row: T) => c.accessor(row) ?? undefined,
            enableSorting: c.sortable ?? false,
            sortUndefined: "last",
            sortFn: "alphanumeric",
          }) as ColumnDef<typeof features, T>,
      ),
    [columns],
  );

  const table = useTable({
    features,
    columns: columnDefs,
    data: rows,
    getRowId: (row: T) => getRowId(row),
    enableSortingRemoval: true,
    state: { sorting: toState(current) },
    onSortingChange: (updater) => {
      const next = typeof updater === "function" ? updater(toState(current)) : updater;
      const first = next[0];
      const value: TableViewSort = first ? { id: first.id, desc: first.desc } : null;
      if (sort === undefined) setOwnSort(value);
      onSortChange?.(value);
    },
  });

  const byId = new Map(columns.map((c) => [c.id, c]));
  const sortedRows = table.getRowModel().rows;
  // WORK-PERF: a long table mounts only the rows near the viewport (row-window.ts).
  const body = React.useRef<HTMLTableSectionElement>(null);
  const w = useRowWindow(body, sortedRows.length, {
    enabled: sortedRows.length > WINDOW_AFTER,
    estimate: 40,
  });
  const spacer = (height: number) =>
    height > 0 ? (
      <tr data-window-spacer="" style={{ height }}>
        <td colSpan={columns.length} className="p-0" />
      </tr>
    ) : null;

  return (
    <div data-slot="table-view" className={cn("min-w-0", className)} {...props}>
      <Table aria-label={label}>
        {caption ? <TableCaption>{caption}</TableCaption> : null}
        <TableHeader>
          {table.getHeaderGroups().map((group) => (
            <TableRow key={group.id} className="hover:bg-transparent">
              {group.headers.map((header) => {
                const col = byId.get(header.column.id);
                const sorted = header.column.getIsSorted();
                return (
                  <TableHead
                    key={header.id}
                    scope="col"
                    numeric={col?.numeric ?? false}
                    className={col?.className}
                    {...(col?.sortable
                      ? {
                          sort:
                            sorted === "asc"
                              ? ("ascending" as const)
                              : sorted === "desc"
                                ? ("descending" as const)
                                : ("none" as const),
                          onSort: () => header.column.toggleSorting(),
                        }
                      : {})}
                  >
                    {col?.header}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody ref={body}>
          {sortedRows.length === 0 ? (
            <TableEmpty
              colSpan={columns.length}
              title={emptyTitle}
              description={emptyDescription}
            />
          ) : null}
          {spacer(w.before)}
          {sortedRows.length === 0
            ? null
            : sortedRows.slice(w.start, w.end).map((row) => {
                const selected = row.id === selectedRowId;
                const extra = rowProps?.(row.original);
                return (
                  <TableRow
                    {...extra}
                    key={row.id}
                    data-row-id={row.id}
                    data-list-row=""
                    data-window-row=""
                    aria-selected={selected || undefined}
                    className={cn("relative", extra?.className)}
                  >
                    {columns.map((col, i) => {
                      const value = col.cell
                        ? col.cell(row.original)
                        : (col.accessor(row.original) ?? "");
                      if (i === 0)
                        return (
                          <TableHead key={col.id} scope="row" className={col.className}>
                            {col.prefix ? (
                              <span className="mr-2 inline-flex items-center gap-1 align-middle">
                                {col.prefix(row.original)}
                              </span>
                            ) : null}
                            {onRowOpen ? (
                              <button
                                type="button"
                                data-row-focus=""
                                onClick={() => onRowOpen(row.original)}
                                aria-current={selected ? "true" : undefined}
                                className={cn(
                                  "cursor-pointer text-left after:absolute after:inset-0 after:content-['']",
                                  FOCUS_RING_INSET,
                                  "focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-ring focus-visible:after:outline-solid focus-visible:outline-none",
                                )}
                              >
                                {value}
                              </button>
                            ) : (
                              value
                            )}
                          </TableHead>
                        );
                      return (
                        <TableCell
                          key={col.id}
                          numeric={col.numeric ?? false}
                          className={cn(selected ? "text-secondary" : undefined, col.className)}
                        >
                          {value}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
          {spacer(w.after)}
        </TableBody>
      </Table>
    </div>
  );
}
